import { useCallback, useMemo, useState } from 'react';
import { getDatabase } from 'firebase/database';
import Modal from './Modal';
import {
  CLEANUP_GROUPS,
  CLEANUP_GROUP_LABELS,
  CLEANUP_GROUP_DESCRIPTIONS,
  CLEANUP_GROUP_BADGES,
  CLEANUP_DEFAULT_RANGE_DAYS,
  CLEANUP_PAGE_SIZE,
  AUDIT_ACTION_TYPES,
  ENTITY_TYPES,
} from '../constants/bookingConst';
import {
  fetchSubmittedEntityIds,
  fetchAuditCoverageStart,
  fetchBookingsInCreatedRange,
  fetchBookingsWithoutCreatedAt,
  classifyBookings,
  deleteDirtyBookings,
} from '../utils/bookingCleanupUtil';
import { logAuditEvent } from '../utils/auditLogUtil';
import { useConfig } from '../context/ConfigContext';
import { spinnerRing } from '../utils/bookingUtils';
import { formatMoney } from '../utils/currencyUtil';
import '../css/AdminCleanupBooking.css';

/**
 * A `datetime-local` input value ("yyyy-MM-ddTHH:mm") for a Date.
 *
 * Built from the LOCAL clock fields rather than toISOString(), which would
 * shift the value by the browser's offset and hand the admin a start time an
 * hour or eight away from the one they meant.
 *
 * @param {Date} date
 * @returns {string}
 */
const toLocalInputValue = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    + `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/**
 * The ISO instant a `datetime-local` value names, read in the browser's own
 * timezone — which on a club's machine is PH time, the same clock the admin
 * reads the audit trail in.
 *
 * @param {string} value - "yyyy-MM-ddTHH:mm"
 * @returns {string|null} ISO 8601 UTC, or null when unparseable
 */
const localInputToIso = (value) => {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : new Date(ms).toISOString();
};

/** Readable local date-time for a stored ISO string. @param {string} iso @returns {string} */
const formatCreatedAt = (iso) => {
  if (!iso) return '—';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return String(iso);
  return new Date(ms).toLocaleString();
};

/**
 * Cleanup Booking — finds booking records that were never submitted through
 * the app and deletes them.
 *
 * THE TEST IS THE AUDIT TRAIL, NOT A HEURISTIC. Every booking made through a
 * screen writes a "Booking submitted" entry; a record injected over the
 * database REST API cannot, because only the app's own code path writes it.
 * So this screen does not guess from names, prices or timing — it asks the
 * audit trail whether each record was ever submitted, and shows the answer.
 *
 * @returns {JSX.Element}
 */
function AdminCleanupBooking() {
  const db = useMemo(() => getDatabase(), []);
  // Only for the currency symbol on the amount column — the club's configured
  // one, so a peso sign is not hardcoded into a screen every client sees.
  const { config } = useConfig();

  const defaultRange = useMemo(() => {
    const end = new Date();
    const start = new Date(end.getTime() - CLEANUP_DEFAULT_RANGE_DAYS * 24 * 60 * 60 * 1000);
    return { start: toLocalInputValue(start), end: toLocalInputValue(end) };
  }, []);

  const [startLocal, setStartLocal] = useState(defaultRange.start);
  const [endLocal, setEndLocal] = useState(defaultRange.end);
  // Off by default: it reads the whole bookings node, which is the one
  // expensive thing this screen can do, so it happens only when asked for.
  const [scanUndated, setScanUndated] = useState(false);
  const [includeAbandoned, setIncludeAbandoned] = useState(false);
  const [includeUndated, setIncludeUndated] = useState(false);

  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [progress, setProgress] = useState(null);
  const [page, setPage] = useState(1);

  // null until the first fetch, so the screen can tell "nothing found" from
  // "nothing asked for yet" — the two would otherwise render identically.
  const [result, setResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [notice, setNotice] = useState(null);

  /**
   * Loads the range, matches it against the audit trail, and classifies.
   * @returns {Promise<void>}
   */
  const handleFetch = useCallback(async () => {
    const startIso = localInputToIso(startLocal);
    const endIso = localInputToIso(endLocal);
    if (!startIso || !endIso) {
      setNotice({ type: 'warning', title: 'Pick a time range', message: 'Both a start and an end date/time are needed before bookings can be fetched.' });
      return;
    }
    if (startIso > endIso) {
      setNotice({ type: 'warning', title: 'Range is backwards', message: 'The start of the range is later than its end. Swap them and try again.' });
      return;
    }

    setLoading(true);
    setPage(1);
    try {
      const [submitted, coverageStart, inRange, undatedResult] = await Promise.all([
        fetchSubmittedEntityIds(db, Date.parse(startIso), Date.parse(endIso)),
        fetchAuditCoverageStart(db),
        fetchBookingsInCreatedRange(db, startIso, endIso),
        scanUndated ? fetchBookingsWithoutCreatedAt(db) : Promise.resolve({ records: [], scannedCount: 0 }),
      ]);
      const submittedIds = submitted.ids;

      const classified = classifyBookings(inRange, submittedIds);
      // Records with no createdAt are classified the same way, but kept in
      // their own bucket — they sit outside every time range by definition,
      // so folding them into the range's results would misreport what the
      // selected range contains.
      const undatedClassified = classifyBookings(undatedResult.records, submittedIds);

      setResult({
        startIso,
        endIso,
        submittedCount: submittedIds.size,
        auditWindowStart: submitted.windowStartMs,
        auditWindowEnd: submitted.windowEndMs,
        auditScanned: submitted.scanned,
        coverageStart,
        dirty: classified.dirty,
        abandoned: classified.abandoned,
        cleanCount: classified.clean.length,
        undated: [...undatedClassified.dirty, ...undatedClassified.abandoned],
        undatedScanned: undatedResult.scannedCount,
        scannedUndated: scanUndated,
      });
      setIncludeAbandoned(false);
      setIncludeUndated(false);
    } catch (error) {
      console.error('Cleanup fetch failed:', error);
      // The two failures worth telling apart, because the fix is different and
      // neither message says so on its own.
      const text = String(error?.message || error);
      const denied = /permission|denied/i.test(text);
      setNotice({
        type: 'error',
        title: denied ? 'The database refused the read' : 'Could not read the bookings',
        message: denied
          ? `${text}\n\nThis project's rules are refusing one of the two reads this screen makes.`
            + '\n\n• /bookings — needs ".read": "auth != null".'
            + '\n• /auditLog — its read rule only allows a query ordered by entityId or timestamp.'
            + '\n\nCheck that this client\'s console rules match database.rules.json in the repo.'
          : `${text}\n\nIf this mentions an index, add "createdAt" to the .indexOn list on /bookings in this project's database rules.`,
      });
    } finally {
      setLoading(false);
    }
  }, [db, startLocal, endLocal, scanUndated]);

  /** Exactly the records the Delete button will remove. @returns {Array<object>} */
  const selectedRecords = useMemo(() => {
    if (!result) return [];
    return [
      ...result.dirty,
      ...(includeAbandoned ? result.abandoned : []),
      ...(includeUndated ? result.undated : []),
    ];
  }, [result, includeAbandoned, includeUndated]);

  /** The rows on screen, in the same order they will be deleted. */
  const visibleRows = useMemo(() => {
    if (!result) return [];
    const rows = [
      ...result.dirty.map(b => ({ ...b, group: CLEANUP_GROUPS.DIRTY, inRange: true })),
      ...result.abandoned.map(b => ({ ...b, group: CLEANUP_GROUPS.ABANDONED, inRange: true })),
      ...result.undated.map(b => ({ ...b, group: CLEANUP_GROUPS.DIRTY, inRange: false })),
    ];
    return rows.sort((a, b) => String(a.createdAt || '').localeCompare(String(b.createdAt || '')));
  }, [result]);

  const totalPages = Math.max(1, Math.ceil(visibleRows.length / CLEANUP_PAGE_SIZE));
  const pageRows = visibleRows.slice((page - 1) * CLEANUP_PAGE_SIZE, page * CLEANUP_PAGE_SIZE);

  /**
   * Deletes the selected records, then re-reads the range so the screen shows
   * what is actually left rather than what it believed a moment ago.
   * @returns {Promise<void>}
   */
  const handleDelete = useCallback(async () => {
    setConfirmOpen(false);
    if (selectedRecords.length === 0) return;
    setDeleting(true);
    setProgress({ done: 0, total: selectedRecords.length });
    try {
      const outcome = await deleteDirtyBookings(db, selectedRecords, setProgress);

      // One entry per run (see BOOKINGS_CLEANED_UP): the records themselves
      // are gone, so this is the only remaining account of what happened.
      await logAuditEvent({
        actionType: AUDIT_ACTION_TYPES.BOOKINGS_CLEANED_UP,
        entityType: ENTITY_TYPES.BOOKING,
        entityId: `cleanup-${Date.now()}`,
        summary: {
          type: 'cleanup',
          deletedCount: outcome.deleted,
          rangeStart: result?.startIso || null,
          rangeEnd: result?.endIso || null,
          includedAbandoned: includeAbandoned,
          includedUndated: includeUndated,
          receiptsRemoved: outcome.receiptsRemoved,
        },
      });

      setNotice({
        type: outcome.failedChunks > 0 ? 'warning' : 'success',
        title: outcome.failedChunks > 0 ? 'Cleanup finished with errors' : 'Cleanup complete',
        message: outcome.failedChunks > 0
          ? `${outcome.deleted} booking record(s) deleted, but ${outcome.failedChunks} batch(es) failed — see the browser console. Fetch again to see what is left.`
          : `${outcome.deleted} booking record(s) deleted, along with their slot locks, equipment holds and payment receipts.`,
      });
      await handleFetch();
    } catch (error) {
      console.error('Cleanup delete failed:', error);
      setNotice({ type: 'error', title: 'Delete failed', message: String(error?.message || error) });
    } finally {
      setDeleting(false);
      setProgress(null);
    }
  }, [db, selectedRecords, result, includeAbandoned, includeUndated, handleFetch]);

  /** Renders one group's heading and explanation. */
  const groupNote = (group, count, extra = null) => (
    <div className={`abc-note abc-note-${group}`}>
      <p className="abc-note-title">{CLEANUP_GROUP_LABELS[group]} — {count}</p>
      <p className="abc-note-body">{CLEANUP_GROUP_DESCRIPTIONS[group]}</p>
      {extra}
    </div>
  );

  const coverageWarning = result?.coverageStart && Date.parse(result.startIso) < result.coverageStart;

  return (
    <div className="abc-screen">
      <div className="abc-intro">
        <h3 className="abc-heading">Cleanup Booking</h3>
        <p className="abc-lede">
          Finds booking records that were never submitted through the app — the signature of records written
          straight into the database — and deletes them along with everything they hold.
        </p>
        <p className="abc-lede abc-lede-quiet">
          How the check works: every booking made through a screen writes a <strong>&quot;Booking submitted&quot;</strong> entry
          to the audit trail the moment it becomes reviewable, whether a guest made it or an admin did. A record
          written directly to the database over the API cannot write that entry. This screen reads both and compares
          them; it does not guess from names, amounts or timing.
        </p>
      </div>

      <div className="abc-controls">
        <div className="abc-field">
          <label htmlFor="abc-start">Created from</label>
          <input
            id="abc-start"
            type="datetime-local"
            value={startLocal}
            onChange={(e) => setStartLocal(e.target.value)}
            disabled={loading || deleting}
          />
        </div>
        <div className="abc-field">
          <label htmlFor="abc-end">Created to</label>
          <input
            id="abc-end"
            type="datetime-local"
            value={endLocal}
            onChange={(e) => setEndLocal(e.target.value)}
            min={startLocal}
            disabled={loading || deleting}
          />
        </div>
        <button type="button" className="abc-btn abc-btn-primary" onClick={handleFetch} disabled={loading || deleting}>
          {loading ? 'Fetching…' : 'Fetch bookings'}
        </button>
        <label className="abc-check abc-check-inline">
          <input
            type="checkbox"
            checked={scanUndated}
            onChange={(e) => setScanUndated(e.target.checked)}
            disabled={loading || deleting}
          />
          <span>
            Also scan for records with no creation date
            <em> — these sit outside every time range, so the only way to find them is to read the whole bookings
              node. Slower, and it counts against the database download quota.</em>
          </span>
        </label>
        <p className="abc-range-note">
          The range is read in this computer&apos;s local time and matched against each booking&apos;s stored
          <code> createdAt</code>.
        </p>
      </div>

      {(loading || deleting) && (
        <div className="abc-loading">
          {spinnerRing(deleting
            ? `Deleting ${progress ? `${progress.done} of ${progress.total}` : ''}…`
            : 'Reading bookings and audit trail…')}
        </div>
      )}

      {!loading && !deleting && !result && (
        <p className="abc-empty">Pick a time range and choose <strong>Fetch bookings</strong> to see what it contains.</p>
      )}

      {!loading && !deleting && result && (
        <>
          <div className="abc-summary">
            <div className="abc-stat abc-stat-dirty">
              <span className="abc-stat-value">{result.dirty.length}</span>
              <span className="abc-stat-label">Dirty — no submit record</span>
            </div>
            <div className="abc-stat">
              <span className="abc-stat-value">{result.abandoned.length}</span>
              <span className="abc-stat-label">Abandoned checkouts</span>
            </div>
            <div className="abc-stat">
              <span className="abc-stat-value">{result.cleanCount}</span>
              <span className="abc-stat-label">Genuine bookings</span>
            </div>
            {result.scannedUndated && (
              <div className="abc-stat">
                <span className="abc-stat-value">{result.undated.length}</span>
                <span className="abc-stat-label">No creation date (outside the range)</span>
              </div>
            )}
          </div>

          {/* What the comparison actually ran against — an admin about to
              delete in bulk should be able to see the evidence, not just the
              verdict. */}
          <p className="abc-range-note">
            Checked {result.auditScanned} audit entr{result.auditScanned === 1 ? 'y' : 'ies'} written
            between {formatCreatedAt(new Date(result.auditWindowStart).toISOString())} and {formatCreatedAt(new Date(result.auditWindowEnd).toISOString())},
            of which {result.submittedCount} were &quot;Booking submitted&quot;. The window is wider than the range
            on purpose: a booking created just before it ends is submitted just after.
          </p>

          {coverageWarning && (
            <div className="abc-warning">
              <p className="abc-note-title">This range reaches back further than the audit trail</p>
              <p className="abc-note-body">
                The oldest audit entry in this project is from {new Date(result.coverageStart).toLocaleString()}.
                Bookings created before then have no &quot;Booking submitted&quot; entry either — not because they are
                fake, but because nothing was recording yet. Check a few rows below against what you remember before
                deleting anything from that period.
              </p>
            </div>
          )}

          {groupNote(CLEANUP_GROUPS.DIRTY, result.dirty.length)}
          {groupNote(
            CLEANUP_GROUPS.ABANDONED,
            result.abandoned.length,
            result.abandoned.length > 0 ? (
              <label className="abc-check">
                <input
                  type="checkbox"
                  checked={includeAbandoned}
                  onChange={(e) => setIncludeAbandoned(e.target.checked)}
                />
                <span>Delete these too ({result.abandoned.length})</span>
              </label>
            ) : null,
          )}
          {groupNote(CLEANUP_GROUPS.CLEAN, result.cleanCount)}

          {result.scannedUndated && result.undated.length > 0 && (
            <div className="abc-note abc-note-dirty">
              <p className="abc-note-title">No creation date — {result.undated.length}</p>
              <p className="abc-note-body">
                These records carry no usable <code>createdAt</code>, so no time range can include them, and they have
                no &quot;Booking submitted&quot; entry either. A record written by a script is under no obligation to
                store a creation date. They are listed below marked <strong>outside range</strong>, and are only
                deleted if you tick this box.
              </p>
              <label className="abc-check">
                <input
                  type="checkbox"
                  checked={includeUndated}
                  onChange={(e) => setIncludeUndated(e.target.checked)}
                />
                <span>Delete these too ({result.undated.length})</span>
              </label>
            </div>
          )}

          <div className="abc-list-head">
            <h4>Records that would be deleted — {selectedRecords.length} of {visibleRows.length} listed</h4>
            <button
              type="button"
              className="abc-btn abc-btn-danger"
              onClick={() => setConfirmOpen(true)}
              disabled={selectedRecords.length === 0}
            >
              Delete Dirty Bookings ({selectedRecords.length})
            </button>
          </div>

          {visibleRows.length === 0 ? (
            <p className="abc-empty">
              Nothing in this range is missing its submit record. Every booking created between those two times came
              through the app.
            </p>
          ) : (
            <>
              <div className="abc-table-wrap">
                <table className="abc-table">
                  <thead>
                    <tr>
                      <th>Created</th>
                      <th>Reference</th>
                      <th>Guest</th>
                      <th>Play date</th>
                      <th>Status</th>
                      <th>Amount</th>
                      <th>Why it is listed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((row) => (
                      <tr key={row.id} className={row.group === CLEANUP_GROUPS.ABANDONED ? 'abc-row-abandoned' : ''}>
                        <td>{row.inRange ? formatCreatedAt(row.createdAt) : <span className="abc-outside">no creation date</span>}</td>
                        <td className="abc-mono">{row.confirmationId || '—'}</td>
                        <td>{row.userName || '—'}<br /><span className="abc-sub">{row.phone || row.email || ''}</span></td>
                        <td>{row.date || '—'}</td>
                        <td>{row.status || '—'}</td>
                        <td>{row.paymentAmount != null ? formatMoney(row.paymentAmount, config) : '—'}</td>
                        <td>
                          <span className={`abc-badge abc-badge-${row.group}`}>{CLEANUP_GROUP_BADGES[row.group]}</span>
                          {!row.inRange && <span className="abc-badge abc-badge-outside">outside range</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="abc-pager">
                  <button type="button" className="abc-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                    Previous
                  </button>
                  <span>Page {page} of {totalPages}</span>
                  <button type="button" className="abc-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      <Modal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Delete these booking records?"
        type="warning"
        confirmText={`Delete ${selectedRecords.length}`}
        showCancel
        cancelText="Cancel"
        onConfirm={handleDelete}
        message={
          `${selectedRecords.length} booking record(s) will be permanently deleted.\n\n`
          + `Created between:\n${result ? formatCreatedAt(result.startIso) : ''} and ${result ? formatCreatedAt(result.endIso) : ''}\n\n`
          + `Included: ${result?.dirty.length || 0} with no submit record`
          + `${includeAbandoned ? `, ${result?.abandoned.length || 0} abandoned checkout(s)` : ''}`
          + `${includeUndated ? `, ${result?.undated.length || 0} with no creation date` : ''}.\n\n`
          + 'Also removed for each one: its payment receipt image, its slot locks and its equipment holds. '
          + 'Audit trail entries are never deleted. This cannot be undone.'
        }
      />

      <Modal
        isOpen={Boolean(notice)}
        onClose={() => setNotice(null)}
        title={notice?.title || ''}
        message={notice?.message || ''}
        type={notice?.type || 'info'}
        confirmText="OK"
      />
    </div>
  );
}

export default AdminCleanupBooking;
